import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, RefreshCw, Clock, CheckCircle, AlertTriangle,
  ChevronLeft, User, Filter, Search, XCircle, ArrowUp, ArrowDown,
  Zap, Plus, Trash2, Edit2, Save, X, Bot
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  open: { color: 'bg-blue-500', text: 'Open' },
  in_progress: { color: 'bg-yellow-500', text: 'In Progress' },
  resolved: { color: 'bg-green-500', text: 'Resolved' },
  closed: { color: 'bg-gray-500', text: 'Closed' }
};

const PRIORITY_CONFIG = {
  low: { color: 'text-gray-400', icon: ArrowDown },
  medium: { color: 'text-blue-400', icon: null },
  high: { color: 'text-orange-400', icon: ArrowUp },
  urgent: { color: 'text-red-500', icon: AlertTriangle }
};

const SupportAdmin = () => {
  const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' or 'autoreplies'
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', priority: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  
  // Auto-reply states
  const [autoReplies, setAutoReplies] = useState([]);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({ keywords: '', response: '', category: '', match_type: 'any', is_active: true });

  useEffect(() => {
    fetchTickets();
    fetchAutoReplies();
  }, [filters]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  // Auto-reply functions
  const fetchAutoReplies = async () => {
    try {
      const res = await api.get('/support/admin/auto-replies');
      setAutoReplies(res.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch auto-replies:', error);
    }
  };

  const createAutoReply = async () => {
    if (!newRule.keywords.trim() || !newRule.response.trim()) {
      toast.error('Keywords and response are required');
      return;
    }
    
    try {
      const payload = {
        keywords: newRule.keywords.split(',').map(k => k.trim()).filter(k => k),
        response: newRule.response,
        category: newRule.category || null,
        match_type: newRule.match_type,
        is_active: newRule.is_active
      };
      await api.post('/support/admin/auto-replies', payload);
      toast.success('Auto-reply rule created!');
      setNewRule({ keywords: '', response: '', category: '', match_type: 'any', is_active: true });
      setShowAddRule(false);
      fetchAutoReplies();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const updateAutoReply = async (ruleId, updates) => {
    try {
      await api.put(`/support/admin/auto-replies/${ruleId}`, updates);
      toast.success('Rule updated!');
      setEditingRule(null);
      fetchAutoReplies();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const deleteAutoReply = async (ruleId) => {
    if (!window.confirm('Delete this auto-reply rule?')) return;
    try {
      await api.delete(`/support/admin/auto-replies/${ruleId}`);
      toast.success('Rule deleted!');
      fetchAutoReplies();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const seedDefaultRules = async () => {
    try {
      const res = await api.post('/support/admin/auto-replies/seed-defaults');
      if (res.data.success) {
        toast.success(res.data.message);
        fetchAutoReplies();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.priority) params.append('priority', filters.priority);
      
      const res = await api.get(`/support/admin/tickets?${params.toString()}`);
      setTickets(res.data.tickets || []);
      setStats(res.data.stats || {});
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (closeTicket = false) => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    try {
      await api.post(`/support/admin/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage,
        status: closeTicket ? 'resolved' : 'in_progress'
      });
      
      // Update local state
      const updatedTicket = {
        ...selectedTicket,
        status: closeTicket ? 'resolved' : 'in_progress',
        messages: [
          ...selectedTicket.messages,
          {
            sender_type: 'admin',
            sender_name: 'Support Team',
            message: replyMessage,
            timestamp: new Date().toISOString()
          }
        ]
      };
      setSelectedTicket(updatedTicket);
      setTickets(tickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setReplyMessage('');
      toast.success(closeTicket ? 'Ticket resolved!' : 'Reply sent!');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.put(`/support/admin/tickets/${ticketId}/status?status=${status}`);
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, status } : t));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status });
      }
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePriority = async (ticketId, priority) => {
    try {
      await api.put(`/support/admin/tickets/${ticketId}/priority?priority=${priority}`);
      setTickets(tickets.map(t => t.id === ticketId ? { ...t, priority } : t));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, priority });
      }
      toast.success(`Priority updated to ${priority}`);
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.subject.toLowerCase().includes(query) ||
      ticket.user_name.toLowerCase().includes(query) ||
      ticket.user_email.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="support-admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-500/20">
              <MessageCircle className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Support Helpdesk</h1>
              <p className="text-sm text-muted-foreground">Manage user support tickets</p>
            </div>
          </div>
          
          <button
            onClick={fetchTickets}
            className="p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-all"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'tickets' ? 'bg-green-500 text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            Support Tickets
          </button>
          <button
            onClick={() => setActiveTab('autoreplies')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'autoreplies' ? 'bg-cyan-500 text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <Bot className="w-5 h-5" />
            Auto-Replies ({autoReplies.length})
          </button>
        </div>

        {activeTab === 'tickets' ? (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <button
            onClick={() => setFilters({ ...filters, status: '' })}
            className={`glass-card p-4 text-center hover:bg-white/5 ${!filters.status ? 'ring-2 ring-primary' : ''}`}
          >
            <p className="text-2xl font-bold">{stats.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </button>
          <button
            onClick={() => setFilters({ ...filters, status: 'open' })}
            className={`glass-card p-4 text-center hover:bg-white/5 ${filters.status === 'open' ? 'ring-2 ring-blue-500' : ''}`}
          >
            <p className="text-2xl font-bold text-blue-500">{stats.open || 0}</p>
            <p className="text-xs text-muted-foreground">Open</p>
          </button>
          <button
            onClick={() => setFilters({ ...filters, status: 'in_progress' })}
            className={`glass-card p-4 text-center hover:bg-white/5 ${filters.status === 'in_progress' ? 'ring-2 ring-yellow-500' : ''}`}
          >
            <p className="text-2xl font-bold text-yellow-500">{stats.in_progress || 0}</p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </button>
          <button
            onClick={() => setFilters({ ...filters, status: 'resolved' })}
            className={`glass-card p-4 text-center hover:bg-white/5 ${filters.status === 'resolved' ? 'ring-2 ring-green-500' : ''}`}
          >
            <p className="text-2xl font-bold text-green-500">{stats.resolved || 0}</p>
            <p className="text-xs text-muted-foreground">Resolved</p>
          </button>
          <button
            onClick={() => setFilters({ ...filters, status: 'closed' })}
            className={`glass-card p-4 text-center hover:bg-white/5 ${filters.status === 'closed' ? 'ring-2 ring-gray-500' : ''}`}
          >
            <p className="text-2xl font-bold text-gray-500">{stats.closed || 0}</p>
            <p className="text-xs text-muted-foreground">Closed</p>
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by subject, user name, or email..."
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-1 space-y-3 max-h-[700px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="glass-card p-6 text-center">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No tickets found</p>
              </div>
            ) : (
              filteredTickets.map(ticket => {
                const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
                const PriorityIcon = priorityConfig.icon;
                
                return (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left glass-card p-4 hover:bg-white/5 transition-all ${
                      selectedTicket?.id === ticket.id ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {PriorityIcon && <PriorityIcon className={`w-4 h-4 ${priorityConfig.color}`} />}
                        <span className={`px-2 py-0.5 rounded-full text-xs text-white ${statusConfig.color}`}>
                          {statusConfig.text}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground capitalize">{ticket.category}</span>
                    </div>
                    <p className="font-medium truncate">{ticket.subject}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span className="truncate">{ticket.user_name}</span>
                      <span>•</span>
                      <span>{new Date(ticket.updated_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Chat View */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <div className="glass-card h-[700px] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                      <h3 className="font-bold">{selectedTicket.subject}</h3>
                      <p className="text-xs text-muted-foreground">
                        From: {selectedTicket.user_name} ({selectedTicket.user_email})
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => updateStatus(selectedTicket.id, e.target.value)}
                      className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => updatePriority(selectedTicket.id, e.target.value)}
                      className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-sm"
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {selectedTicket.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.sender_type === 'admin'
                          ? 'bg-blue-500/20 rounded-tr-none'
                          : 'bg-green-500/20 rounded-tl-none'
                      }`}>
                        <p className="text-xs font-medium mb-1">
                          {msg.sender_type === 'admin' ? 'You (Support)' : msg.sender_name}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(msg.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                {selectedTicket.status !== 'closed' && (
                  <div className="p-4 border-t border-white/10">
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your response..."
                      rows={3}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3"
                      disabled={sending}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => sendReply(false)}
                        disabled={sending || !replyMessage.trim()}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 transition-all"
                      >
                        <Send className="w-4 h-4" />
                        Send Reply
                      </button>
                      <button
                        onClick={() => sendReply(true)}
                        disabled={sending || !replyMessage.trim()}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-all"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Send & Resolve
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-card h-[700px] flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a ticket to respond</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
        ) : (
        /* Auto-Replies Tab */
        <div className="space-y-6">
          {/* Auto-Reply Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Auto-Reply Rules</h2>
              <p className="text-sm text-muted-foreground">Automatic responses based on keywords (FREE - no AI)</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={seedDefaultRules}
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-xl hover:bg-purple-500/30 transition-all"
              >
                <Zap className="w-5 h-5 text-purple-500 inline mr-2" />
                Load Defaults
              </button>
              <button
                onClick={() => setShowAddRule(true)}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Rule
              </button>
            </div>
          </div>

          {/* Add/Edit Rule Form */}
          {(showAddRule || editingRule) && (
            <div className="glass-card p-6">
              <h3 className="font-bold mb-4">{editingRule ? 'Edit Rule' : 'Add New Auto-Reply Rule'}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Keywords (comma separated)</label>
                  <input
                    type="text"
                    value={editingRule ? editingRule.keywords.join(', ') : newRule.keywords}
                    onChange={(e) => editingRule 
                      ? setEditingRule({...editingRule, keywords: e.target.value.split(',').map(k => k.trim())})
                      : setNewRule({...newRule, keywords: e.target.value})
                    }
                    placeholder="password, reset, forgot, login"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Trigger words that activate this response</p>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Auto Response</label>
                  <textarea
                    value={editingRule ? editingRule.response : newRule.response}
                    onChange={(e) => editingRule 
                      ? setEditingRule({...editingRule, response: e.target.value})
                      : setNewRule({...newRule, response: e.target.value})
                    }
                    placeholder="Your automated response message..."
                    rows={4}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Category (optional)</label>
                    <select
                      value={editingRule ? editingRule.category || '' : newRule.category}
                      onChange={(e) => editingRule 
                        ? setEditingRule({...editingRule, category: e.target.value})
                        : setNewRule({...newRule, category: e.target.value})
                      }
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    >
                      <option value="">All Categories</option>
                      <option value="general">General</option>
                      <option value="technical">Technical</option>
                      <option value="billing">Billing</option>
                      <option value="complaint">Complaint</option>
                      <option value="suggestion">Suggestion</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Match Type</label>
                    <select
                      value={editingRule ? editingRule.match_type : newRule.match_type}
                      onChange={(e) => editingRule 
                        ? setEditingRule({...editingRule, match_type: e.target.value})
                        : setNewRule({...newRule, match_type: e.target.value})
                      }
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    >
                      <option value="any">Any Keyword</option>
                      <option value="all">All Keywords</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Status</label>
                    <button
                      onClick={() => editingRule 
                        ? setEditingRule({...editingRule, is_active: !editingRule.is_active})
                        : setNewRule({...newRule, is_active: !newRule.is_active})
                      }
                      className={`w-full px-4 py-2 rounded-xl font-medium ${
                        (editingRule ? editingRule.is_active : newRule.is_active) 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-500 text-white'
                      }`}
                    >
                      {(editingRule ? editingRule.is_active : newRule.is_active) ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => { setShowAddRule(false); setEditingRule(null); }}
                    className="px-4 py-2 border border-white/20 rounded-xl hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => editingRule 
                      ? updateAutoReply(editingRule.id, {
                          keywords: editingRule.keywords,
                          response: editingRule.response,
                          category: editingRule.category || null,
                          match_type: editingRule.match_type,
                          is_active: editingRule.is_active
                        })
                      : createAutoReply()
                    }
                    className="px-4 py-2 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600"
                  >
                    <Save className="w-4 h-4 inline mr-2" />
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rules List */}
          <div className="space-y-3">
            {autoReplies.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <Bot className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No auto-reply rules yet</p>
                <button
                  onClick={seedDefaultRules}
                  className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600"
                >
                  Load Default Rules
                </button>
              </div>
            ) : (
              autoReplies.map(rule => (
                <div 
                  key={rule.id} 
                  className={`glass-card p-4 ${rule.is_active ? 'border-green-500/30' : 'border-gray-500/30 opacity-60'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {rule.keywords.map((kw, idx) => (
                          <span key={idx} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs">
                            {kw}
                          </span>
                        ))}
                        {rule.category && (
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs capitalize">
                            {rule.category}
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          rule.match_type === 'all' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {rule.match_type === 'all' ? 'Match All' : 'Match Any'}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-muted-foreground">{rule.response}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateAutoReply(rule.id, { is_active: !rule.is_active })}
                        className={`p-2 rounded-lg ${rule.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}
                        title={rule.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <Zap className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteAutoReply(rule.id)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Info Box */}
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
            <p className="text-green-500 text-sm">
              <b>💡 How it works:</b> When a user creates a ticket or sends a message containing any of the keywords, 
              the system automatically sends the configured response. This is 100% FREE - no AI involved!
            </p>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default SupportAdmin;
